import React from 'react';
import ReactDOMServer from 'react-dom/server';
import SafeMarkdown from '../../Common/SafeMarkdown';
import { getGenreValues } from '../../../config/genres';
import { GRADE_LEVELS_ASSESSMENT, type GradeLevelAssessment } from '../../../constants/grades';
import type { PassageData, Question } from './assessmentsTypes';

export const GENRES = getGenreValues();

export type GradeLevel = GradeLevelAssessment;
export const GRADE_LEVELS: GradeLevel[] = [...GRADE_LEVELS_ASSESSMENT];

export const processStreamingContent = (buffer: string, newContent: string): { 
  completeContent: string; 
  remainingBuffer: string; 
  hasNewParagraphs: boolean 
} => {
  const fullContent = buffer + newContent;
  
  // Split by double newlines to identify paragraphs
  const paragraphs = fullContent.split(/\n\s*\n/);
  
  // If we have multiple paragraphs, the last one might be incomplete
  if (paragraphs.length > 1) {
    // All but the last paragraph are complete
    const completeParagraphs = paragraphs.slice(0, -1);
    const incompleteParagraph = paragraphs[paragraphs.length - 1];
    
    // Check if the incomplete paragraph ends with a sentence (period, exclamation, question mark)
    const sentenceEndRegex = /[.!?]\s*$/;
    if (sentenceEndRegex.test(incompleteParagraph)) {
      // The last paragraph is also complete
      return {
        completeContent: completeParagraphs.join('\n\n') + '\n\n' + incompleteParagraph,
        remainingBuffer: '',
        hasNewParagraphs: true
      };
    } else {
      // Keep the last paragraph in buffer
      return {
        completeContent: completeParagraphs.join('\n\n'),
        remainingBuffer: incompleteParagraph,
        hasNewParagraphs: completeParagraphs.length > 0
      };
    }
  } else {
    // Only one paragraph, check if it's complete
    const paragraph = paragraphs[0];
    const sentenceEndRegex = /[.!?]\s*$/;
    
    if (sentenceEndRegex.test(paragraph)) {
      return {
        completeContent: paragraph,
        remainingBuffer: '',
        hasNewParagraphs: true
      };
    } else {
      return {
        completeContent: '',
        remainingBuffer: fullContent,
        hasNewParagraphs: false
      };
    }
  }
};

export const lexileRanges: Record<GradeLevel, string> = {
  'K': '0L-200L',
  '1': '200L-400L',
  '2': '300L-500L',
  '3': '500L-700L',
  '4': '600L-800L',
  '5': '700L-900L',
  '6': '800L-1000L',
  '7': '850L-1050L',
  '8': '900L-1100L',
  '9': '950L-1150L',
  '10': '1000L-1200L',
  '11': '1050L-1250L',
  '12': '1075L-1275L',
  'Reading': '950L-1250L',
  'Speech': '950L-1250L'
};

// --- Updated styles for Markdown ---
export const markdownStyles = `
  .markdown-body {
    font-family: Arial, sans-serif;
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
  }

  /* Add page break controls */
  .passage-section {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .question-block {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .answer-choices {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .answer-key-item {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Force page breaks between major sections */
  .next-passage {
    page-break-before: always;
    break-before: page;
  }

  .questions-section {
    page-break-before: always;
    break-before: page;
  }

  .answer-key-section {
    page-break-before: always;
    break-before: page;
  }

  /* Keep headings with their content */
  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* Keep paragraphs together */
  p {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .markdown-body h1, .markdown-body h2, .markdown-body h3 {
    color: #333;
    font-weight: bold;
    margin: 1em 0 0.5em;
  }

  .markdown-body h1 {
    text-align: center;
    font-size: 1.5em;
  }

  /* Questions section */
  .markdown-body h2:not(.answer-key-section h2) {
    margin-top: 2em;
    font-size: 1.3em;
    border-bottom: 2px solid #333;
    padding-bottom: 0.5em;
  }

  /* Numbered questions */
  .markdown-body ol {
    list-style-type: none;
    counter-reset: question;
    padding-left: 0;
    margin-top: 1.5em;
  }

  .markdown-body ol > li {
    counter-increment: question;
    margin-bottom: 2em;
    padding-left: 2em;
    position: relative;
  }

  .markdown-body ol > li::before {
    content: counter(question) ".";
    position: absolute;
    left: 0;
    font-weight: bold;
  }

  /* Answer choices (A, B, C, D) */
  .markdown-body ol > li > ol {
    list-style-type: none;
    margin: 1em 0 1em 0;
    padding-left: 2em;
  }

  .markdown-body ol > li > ol > li {
    margin: 0.5em 0;
    position: relative;
    padding-left: 1.5em;
    display: block;
  }

  .markdown-body ol > li > ol > li::before {
    content: attr(value) ".";
    position: absolute;
    left: 0;
    font-weight: normal;
  }

  /* Answer key section */
  .answer-key-section {
    page-break-before: always;
    margin-top: 2em;
    border-top: 2px solid #333;
    padding-top: 2em;
  }

  .answer-key-section h2 {
    text-align: center;
    margin-bottom: 1em;
  }

  /* Print-specific styles */
  @media print {
    .answer-key-section {
      page-break-before: always !important;
    }
    .markdown-body {
      padding: 0;
    }
  }

  /* Drama formatting */
  .drama-line {
    margin: 0.4em 0;
  }
  .drama-character {
    font-weight: bold;
    margin-right: 0.35em;
  }
  .drama-direction {
    font-style: italic;
    text-align: center;
    margin: 0.6em 0;
    display: block;
  }
  .drama-scene {
    text-align: center;
    font-weight: bold;
    margin: 1em 0 0.6em 0;
    display: block;
  }

  /* PDF-specific controls */
  .pdf-container {
    width: 8.5in;
    margin: 0 auto;
    background: white;
  }

  .question-wrapper {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: block;
    margin-bottom: 2em;
  }

  .answer-wrapper {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: block;
  }

  /* Strengthen existing break controls */
  .passage-section,
  .question-block,
  .answer-choices,
  .answer-key-item {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: block;
  }

  /* Keep list items together */
  li {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Force minimum space before breaks */
  .question-block {
    min-height: 2em;
  }

  .answer-choices {
    min-height: 1em;
  }

  @media print {
    .pdf-container {
      width: 100%;
      max-width: 8.5in;
    }
    
    /* Reinforce break controls */
    .question-wrapper,
    .answer-wrapper,
    .passage-section,
    .question-block,
    .answer-choices,
    .answer-key-item,
    li {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  }
`;

// Drama helpers
const isSceneHeading = (line: string): boolean => {
  const trimmed = line.trim();
  return /^(ACT\s+[IVXLC]+|SCENE\s+\d+|Scene\s+\d+|Act\s+[IVXLC]+)\b/.test(trimmed);
};

const isStageDirectionLine = (line: string): boolean => {
  const t = line.trim();
  return (t.startsWith('(') && t.endsWith(')')) || (t.startsWith('[') && t.endsWith(']'));
};

const parseCharacterLine = (line: string): { name: string; dialog: string } | null => {
  // Match uppercase name(s) followed by a colon
  const match = line.match(/^([A-Z][A-Z\s\-']{0,30}):\s*(.*)$/);
  if (!match) return null;
  const name = match[1].trim();
  const dialog = match[2];
  return { name, dialog };
};

// Convert drama text to HTML blocks for print/PDF/Word
const formatDramaForHtml = (text: string): string => {
  const lines = text.split(/\r?\n/);
  const htmlLines: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.replace(/^\s+|\s+$/g, '');
    if (line === '') {
      htmlLines.push('<div class="drama-line">&nbsp;</div>');
      continue;
    }
    if (isSceneHeading(line)) {
      htmlLines.push(`<div class="drama-scene">${line}</div>`);
      continue;
    }
    if (isStageDirectionLine(line)) {
      const inner = line.replace(/^\(|^\[/, '').replace(/\)$|\]$/, '');
      htmlLines.push(`<div class="drama-direction">${inner}</div>`);
      continue;
    }
    const parsed = parseCharacterLine(line);
    if (parsed) {
      htmlLines.push(
        `<div class="drama-line"><span class="drama-character">${parsed.name}:</span><span>${parsed.dialog}</span></div>`
      );
      continue;
    }
    // Default paragraph line
    htmlLines.push(`<div class="drama-line">${line}</div>`);
  }
  return htmlLines.join('\n');
};

// Convert drama text to Markdown for on-screen SafeMarkdown rendering
export const formatDramaForMarkdown = (text: string, title: string): string => {
  // Remove title if present at the start
  const titleRegex = new RegExp(`^(#+\\s*)?${title.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\s*\\n*`, 'i');
  const withoutTitle = text.replace(titleRegex, '').trim();
  const lines = withoutTitle.split(/\r?\n/);
  const mdLines: string[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === '') {
      mdLines.push('');
      continue;
    }
    if (isSceneHeading(line)) {
      mdLines.push(`### ${line}`);
      continue;
    }
    if (isStageDirectionLine(line)) {
      mdLines.push(`*${line.substring(1, line.length - 1)}*`);
      continue;
    }
    const parsed = parseCharacterLine(line);
    if (parsed) {
      mdLines.push(`**${parsed.name}:** ${parsed.dialog}`);
      continue;
    }
    mdLines.push(line);
  }
  return `# ${title}\n\n${mdLines.join('\n\n')}`;
};

export const splitContent = (content: string, isPaired: boolean = false) => {
  console.log("\n=== Splitting Content ===");
  
  if (isPaired) {
    const passages = content.split('[[PASSAGE_BREAK]]');
    const answerKeyStart = passages[passages.length - 1].indexOf('[[ANSWER_KEY_START]]');
    
    if (answerKeyStart !== -1) {
      const mainContent = passages.slice(0, -1).join('[[PASSAGE_BREAK]]') + 
        passages[passages.length - 1].slice(0, answerKeyStart);
      const answerKeyContent = passages[passages.length - 1].slice(answerKeyStart + '[[ANSWER_KEY_START]]'.length);
      return {
        mainContent: mainContent.trim(),
        answerKeyContent: answerKeyContent.trim()
      };
    }
    return {
      mainContent: content.trim(),
      answerKeyContent: ""
    };
  }
  
  // Original single passage logic
  const marker = '[[ANSWER_KEY_START]]';
  const parts = content.split(marker);
  
  if (parts.length > 1) {
    return {
      mainContent: parts[0].trim(),
      answerKeyContent: parts[1].trim()
    };
  }
  
  return {
    mainContent: content.trim(),
    answerKeyContent: ""
  };
};

// A helper to embed the main content + answer key together.
export const createPrintContent = (
  passages: PassageData[],
  combinedQuestions: Question[] | undefined,
  answerKey: string | undefined,
  isPaired: boolean,
  format: 'print' | 'pdf' | 'word' = 'print',
  teksCodes?: string[]
) => {
  const formatPassageContent = (content: string, title: string, genre?: string) => {
    // Remove the title from the beginning of content if it exists
    let cleanContent = content;
    
    // Remove title at the start (with or without #)
    const titleRegex = new RegExp(`^(#+\\s*)?${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i');
    cleanContent = cleanContent.replace(titleRegex, '').trim();
    
    // Drama-specific formatting
    if (genre && /drama/i.test(genre)) {
      return formatDramaForHtml(cleanContent);
    }
    
    // Split content into paragraphs and wrap each in a p tag
    return cleanContent
      .split(/\n\s*\n/) // Split on empty lines
      .filter(para => para.trim()) // Remove empty paragraphs
      .map(paragraph => `<p>${paragraph.trim()}</p>`)
      .join('\n');
  };

  const formatQuestionsForWord = (questions: Question[], sectionTitle: string, startingNumber: number) => {
    let questionNumber = startingNumber;
    return `
      <div class="questions-section">
        <h2>${sectionTitle}</h2>
        ${questions?.map((q) => {
          const currentNumber = questionNumber++;
          return `
            <div style="margin-bottom: 2em;">
              <p class="question-text"><strong>${currentNumber}. ${q.question}</strong></p>
              <div style="margin-left: 1em; margin-top: 0.5em;">
                ${q.answers?.map((answer, j) => {
                  let cleanAnswer = typeof answer === 'object' && answer !== null && 'text' in answer
                    ? answer.text
                    : String(answer);
                  // Remove any leading letter prefix (e.g., "A. ", "B) ", "C: ") to avoid duplication
                  cleanAnswer = cleanAnswer.replace(/^[A-Da-d][\.\)\:\s]+\s*/, '');
                  return `
                    <div style="display: flex; margin-bottom: 0.5em; align-items: flex-start;">
                      <span style="font-weight: bold; margin-right: 0.3em; flex-shrink: 0;">${String.fromCharCode(65 + j)}.</span>
                      <span style="flex: 1;">${cleanAnswer}</span>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  };

  const formatAnswerKeyForWord = () => {
    if (!answerKey) return '';

    const rawLines = answerKey.split('\n');
    const stripMd = (s: string) => (s || '').replace(/[**_`]/g, '').trim();
    const lines = rawLines.map(ln => stripMd(ln));
    let formattedAnswerKey = '<div class="answer-key-section">\n<h2>Answer Key</h2>\n';
    formattedAnswerKey += '<table border="0" cellspacing="0" cellpadding="0" style="width:100%">\n';

    const isBoundary = (line: string) => /^(\d+[\).]|question\s*\d+\b|correct\s*answer\s*:)/i.test((line || '').trim());

    const results: { answer: string; explanation: string }[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const answerMatch = line.match(/^\s*(?:Correct\s*Answer|Answer)\s*:\s*([A-D])\b/i);
      if (answerMatch) {
        const answer = answerMatch[1].toUpperCase();
        let explanation = '';
        // Inline explanation on same line
        const inlineExpl = line.match(/Explanation\s*:\s*(.+)$/i);
        if (inlineExpl) {
          explanation = inlineExpl[1];
        } else {
          // Look for next Explanation line
          let j = i + 1;
          for (; j < lines.length; j++) {
            const es = lines[j].match(/^\s*Explanation\s*:\s*(.*)$/i);
            if (es) {
              explanation = es[1] || '';
              j++;
              // Accumulate until boundary
              for (; j < lines.length; j++) {
                if (isBoundary(lines[j])) break;
                if (lines[j]) explanation += (explanation ? ' ' : '') + lines[j];
              }
              break;
            }
            if (isBoundary(lines[j])) break;
          }
        }
        explanation = stripMd(explanation);
        results.push({ answer, explanation });
      }
    }

    results.forEach((r, idx) => {
      formattedAnswerKey += `
        <tr>
          <td colspan="2" style="padding-top: 12pt">
            <p class="answer-key-item">${idx + 1}) ${r.answer ? `<strong>${r.answer}</strong>` : ''}${r.explanation ? `, Explanation: ${r.explanation}` : ''}</p>
          </td>
        </tr>`;
    });

    if (teksCodes && teksCodes.length > 0) {
      const uniqueCodes = Array.from(new Set(teksCodes.filter(Boolean)));
      if (uniqueCodes.length) {
        formattedAnswerKey += `
          <tr>
            <td colspan="2" style="padding-top: 12pt">
              <p class="answer-key-item"><em>Aligned to TEKS: ${uniqueCodes.join(', ')}</em></p>
            </td>
          </tr>`;
      }
    }

    formattedAnswerKey += '</table></div>';
    return formattedAnswerKey;
  };

  let content = '';
  let questionCounter = 0;
  
  if (format === 'word') {
    content = '<div class="WordSection1">';
    // Add passages with Word-specific formatting
    passages.forEach((passage, index) => {
      const cleanTitle = passage.title.replace(/^#+\s*/, '').trim();
      content += `
        <div class="passage-section">
          <h1 class="passage-title">${cleanTitle}</h1>
          <div class="passage-content">
            ${formatPassageContent(passage.passage, passage.title, passage.genre)}
          </div>
          ${passage.questions && passage.questions.length > 0 
            ? formatQuestionsForWord(passage.questions, `Questions for Passage ${index + 1}`, questionCounter + 1)
            : ''}
        </div>
      `;
      questionCounter += passage.questions?.length || 0;
    });

    // Add combined questions for paired passages
    if (isPaired && combinedQuestions && combinedQuestions.length > 0) {
      content += formatQuestionsForWord(combinedQuestions, 'Combined Questions', questionCounter + 1);
    }

    // Add answer key
    if (answerKey) {
      content += formatAnswerKeyForWord();
    }

    content += '</div>';
  } else {
    // Original formatting for print and PDF
    content = '<div class="markdown-body">';
    // Add passages with format-specific styling
    passages.forEach((passage, index) => {
      // Clean the title by removing any '#' and extra spaces
      const cleanTitle = passage.title.replace(/^#+\s*/, '').trim();
      
      content += `
        <div class="passage-section ${index > 0 ? 'next-passage' : ''}">
          <h1 class="passage-title">${cleanTitle}</h1>
          <div class="passage-content">
            ${formatPassageContent(passage.passage, passage.title, passage.genre)}
          </div>
          ${passage.questions && passage.questions.length > 0 
            ? formatQuestionsForWord(passage.questions, `Questions for Passage ${index + 1}`, questionCounter + 1)
            : ''}
        </div>
      `;
      questionCounter += passage.questions?.length || 0;
    });

    // Add combined questions for paired passages
    if (isPaired && combinedQuestions && combinedQuestions.length > 0) {
      content += formatQuestionsForWord(combinedQuestions, 'Combined Questions', questionCounter + 1);
    }

    content += '</div>';

    // Add answer key with proper formatting
    if (answerKey) {
      content += formatAnswerKeyForWord();
    }
  }

  return content;
};

// Utility to convert raw Markdown into rendered HTML using react-markdown + SSR
export function convertMarkdownToHtml(markdown: string) {
  return ReactDOMServer.renderToStaticMarkup(
    React.createElement(SafeMarkdown, null, markdown),
  );
}
